<template>
    <div class="test-wrapper">
        <!-- test entry page -->
        <section v-if="!testPreview">
            {{ $t('MAIN_CONTENT.testTaker.loading') }}
        </section>
        <section v-else-if="testPreview && !testEndTime && !testStartTime">
            <div class="pre-test">
                <div class="display-table">
                    <div>
                        <div class="header text-center">
                            <h1>{{ testPreview.TEST.TEST_TITLE }}</h1>
                        </div>
                        <div class="content">
                            <p v-if="testPreview.TEST.TEST_DESCRIPTION" v-html="testPreview.TEST.TEST_DESCRIPTION"></p>

                            <div class="test-meta">
                                <div>
                                    <label>{{ $t('MAIN_CONTENT.testTaker.takeTester_windowOpen') }}</label>
                                    <div class="pill pill-gray-light">{{ testPreview.TEST.TEST_WINDOW_OPEN | datetime }}</div>
                                </div>
                                <div>
                                    <label>{{ $t('MAIN_CONTENT.testTaker.takeTester_windowCloses') }}</label>
                                    <div class="pill pill-gray-light">{{ testPreview.TEST.TEST_WINDOW_CLOSED | datetime }}</div>
                                </div>
                                <div v-if="testPreview.TEST.TIME_LIMIT">
                                    <label>{{ $t('MAIN_CONTENT.testTaker.takeTester_duration') }}</label>
                                    <div class="pill pill-gray-light">
																			{{ $t('MAIN_CONTENT.testTaker.takeTester_mins', { time: testPreview.TEST.TIME_LIMIT } ) }}
																		</div>
                                </div>
                            </div>
                            <div
                                v-if="testEnded"
                                class="alert-panel alert-warning">
                                <div class="alert-bar">
                                    <i class="fas fa-info-circle" />
                                </div>
                                <div class="alert-message">
                                    {{ $t('MAIN_CONTENT.testTaker.takeTester_testEndedMessage') }}
                                </div>
                            </div>
                            <div
                                v-else-if="testExpired"
                                class="alert-panel alert-warning">
                                <div class="alert-bar">
                                    <i class="fas fa-info-circle" />
                                </div>
                                <div class="alert-message">
                                    {{ $t('MAIN_CONTENT.testTaker.takeTester_testExpired') }}
                                </div>
                            </div>
                            <div
                                v-else-if="testPreview.TEST.SINGLE_SITTING_COMPLETION_FLG || testPreview.TEST.STUDENT_IMMEDIATE_FEEDBACK"
                                class="alert-panel alert-info">
                                <div class="alert-bar">
                                    <i class="fas fa-info-circle" aria-hidden="true" />
                                </div>
                                <div class="alert-message">
                                    <p v-if="testPreview.TEST.SINGLE_SITTING_COMPLETION_FLG">
																			{{ $t('MAIN_CONTENT.testTaker.takeTester_singleSession') }}
																		</p>
                                    <p v-if="testPreview.TEST.STUDENT_IMMEDIATE_FEEDBACK">
                                        {{ $t('MAIN_CONTENT.testTaker.takeTester_immediateFeedback') }}
                                        <span v-if="!testPreview.TEST.ALLOW_ANSWER_CHANGE">
																					{{ $t('MAIN_CONTENT.testTaker.takeTester_allowChange') }}
																				</span>
                                    </p>
                                    
                                </div>
                            </div>
                            <div class="foo text-center" v-if="!testEnded && !testExpired">
                                <button
                                    class="btn btn-success"
                                    @click="beginTest()">
                                    <font-awesome-icon
                                    icon="stopwatch" /> {{ $t('MAIN_CONTENT.testTaker.takeTester_startNow') }}
                                </button>
                            </div>
                            <div class="foo text-center" v-else>
                                <button
                                    class="btn btn-primary"
                                    @click="closeWindow()">
																	{{ $t('MAIN_CONTENT.testTaker.takeTester_closeWindow') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        
        <!-- test started -->
        <section v-if="testPreview && !testEnded && !testEndTime && testStartTime">
            <div class="test-header">
                <h1>{{ testPreview.TEST.TEST_TITLE }}</h1>
                <div v-if="this.mode !== 'proxy' && testTimeRemaining > -1" class="float-right">
                    <i :class="`fas fa-stopwatch ${ testTimeRemaining > 0 ? 'animate-flicker' : '' }`" aria-hidden="true" ></i> 
                    <span>
                        {{ testTimeRemaining }}
                    </span>
                    {{ $t('MAIN_CONTENT.testTaker.takeTester_minsLeft') }}
                </div>
            </div> 

            <div class="row">
                <div class="col-md-9">
                    <div id="testZone" class="testing-zone" tabindex="-1">
                        <question-group
                            v-if="activeQuestion && activeQuestion.QUESTION_TYPE && !timeLimitExceeded"
                            :group="activeQuestionGroup" />
                        
                        <div v-if="activeQuestion && activeQuestion.QUESTION_TYPE && !timeLimitExceeded" :aria-live="activeQuestion.SEQUENCE === 1 ? 'off' : 'assertive' ">
                            <div class="extra-credit">
                                <span 
                                    v-if="activeQuestion.EXTRA_CREDIT_FLG"
                                    class="pill pill-success-lighten">
																	<font-awesome-icon
                                    v-if="activeQuestion.EXTRA_CREDIT_FLG" 
                                    icon="certificate" 
                                    size="2x"
                                    title="Extra Credit Question" /> 
																	<span class="hidden-xs">
																			{{ $t('MAIN_CONTENT.testReviewer.extraCredit') }}
																	</span>
                                </span>
                            </div>
                            <multiple-choice-question
                                v-if="activeQuestion.QUESTION_TYPE == 'C'"
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :testMode="'student'" />

                            <true-false-question
                                v-if="activeQuestion.QUESTION_TYPE == 'T'" 
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :testMode="'student'"/>

                            <short-fill-question
                                v-if="activeQuestion.QUESTION_TYPE == 'S'"
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :blankAnswer="blankAnswer"
                                :testMode="'student'" />

                            <matching-question
                                v-if="activeQuestion.QUESTION_TYPE == 'M'" 
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :questions="questions"
                                :answers="answers"
                                :test="test"
                                :testMode="'student'"/>

                            <numeric-question
                                v-if="activeQuestion.QUESTION_TYPE == 'N'" 
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :blankAnswer="blankAnswer"
                                :testMode="'student'"/>

                            <essay-question
                                v-if="activeQuestion.QUESTION_TYPE == 'E'" 
                                :testPreview="testPreview"
                                :activeQuestion="activeQuestion"
                                :studentAnswer="studentAnswer"
                                :studentAnswers="studentAnswers"
                                :blankAnswer="blankAnswer"
                                :testMode="'student'"/>

                        </div>
                        <!-- display the confirm panel -->
                        <div
                            v-else
                            class="test-confirm" aria-live="assertive" aria-label="confirm submission">
                            <confirm-submission />
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <aside>
                        <div class="question-nav">
                            <question-selector />
                        </div>
                        <!-- if immediate scoring is on, show running total -->
                        <div
                            v-if="testPreview.TEST.STUDENT_IMMEDIATE_FEEDBACK"
                            class="score-wrap text-center">
													<span class="pill pill-dark">
														<strong>
															{{ $t('MAIN_CONTENT.testReviewer.scoreOutOfTotal', { score: totalPointsEarned, total: testPreview.TEST.TOTAL_SCORE } ) }}
														</strong> {{ $t('MAIN_CONTENT.testReviewer.points') }} 
													</span>
                        </div>
                    </aside>
                </div>
                
                
                
                
            </div>
        </section>

        <!-- test completed -->
        <section v-if="testStartTime && testEndTime">
            <div class="post-test">
                <div class="display-table">
                    <div class="header text-center">
                        <h2>{{ testPreview.TEST.TEST_TITLE }}</h2>
                    </div>
                    <div class="content">
                        <p><b>{{ $t('MAIN_CONTENT.testTaker.takeTester_submitted') }}</b></p>
                        <p v-if="testPreview.TEST.ANSWERS_VISIBLE_AFTER_GRADING">
                            {{ $t('MAIN_CONTENT.testTaker.takeTester_availableForReview') }}
                        </p>
                        <div class="foo text-center">
                            <button
                                class="btn btn-primary"
                                @click="closeWindow()">
                                {{ $t('MAIN_CONTENT.testTaker.takeTester_closeWindow') }}
                            </button>
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import questionGroup from "./questionGroup.vue";
import questionSelector from "./questionSelector.vue";
import multipleChoiceQuestion from "./multipleChoiceQuestion.vue";
import trueFalseQuestion from "./trueFalseQuestion.vue";
import shortFillQuestion from "./shortFillQuestion.vue";
import matchingQuestion from "./matchingQuestion.vue";
import numericQuestion from "./numericQuestion.vue";
import essayQuestion from "./essayQuestion.vue";
import confirmSubmission from "./confirmSubmission.vue";
import TestTakerResource from '../../api/TestTaker';

const mapSetter = (state, setters = {}) => (
  Object.keys(state).reduce((acc, stateName) => {
    acc[stateName] = {
      get: state[stateName],
   };
   // check if setter exists
   if (setters[stateName]) {
      acc[stateName].set = setters[stateName];
   }

   return acc;
 }, {})
);

export default{
    components: { 
        questionGroup, 
        questionSelector, 
        multipleChoiceQuestion, 
        trueFalseQuestion, 
        shortFillQuestion, 
        matchingQuestion,
        numericQuestion,
        essayQuestion, 
        confirmSubmission
    },
    props: {
        mode: {
            type    : String,
            default : ""
        }
    },
    data (){
        return{
            testStarted       : false,
            testTimeRemaining : null,
            timer             : null
        };
    },
    computed: {
        ...mapSetter(mapState( {
            testPreview: state =>{
                return state.testTaker.testPreview;
            },
            totalPointsEarned: state =>{
                return state.testTaker.totalPointsEarned;
            },
            activeQuestion: state =>{
                return state.testTaker.activeQuestion;
            },
            testStartTime: state =>{
                return state.testTaker.testAttemptStartTime;
            },
            testEndTime: state =>{
                return state.testTaker.testAttemptEndTime;
            },
            timeLimitExceeded: state =>{
                return state.testTaker.timeLimitExceeded;
            },
            studentAnswers: state =>{
                return state.testTaker.testPreview.STUDENT_ATTEMPT.ANSWERS;
            },
            blankAnswer:state =>{
                return state.testTaker.blankAnswerObj;
            },
            questions: state =>{
                return state.testTaker.testPreview.QUESTIONS;
            },
            answers: state =>{
                return state.testTaker.testPreview.ANSWERS;
            },
            test: state =>{
                return state.testTaker.testPreview.TEST;
            }
        } )),
        blankAnswer() {
            return {
                "TEST_RID"                 : parseInt( this.testPreview.TEST.TEST_RID ),
                "TEST_QUESTION_RID"        : parseInt( this.activeQuestion.TEST_QUESTION_RID ),
                "TEST_QUESTION_PARENT_RID" : parseInt( this.activeQuestion.TEST_QUESTION_PARENT_RID),
                "TOTAL_POINTS"             : 0,
                "TEACHER_RID"              : "",
                "QUESTION_GRADED_STATUS"   : "",
                "QUESTION_TYPE"            : "",
                "ANSWER_FORMAT_TYPE"       : "",
                "SUB_QUESTIONS"            : {},
                "ACTUAL_ANSWER"            : "",
                "ANSWER_INFO"              : {
                    "QUESTION_ANSWERED_BY"      : "",
                    "QUESTION_GENERAL_FEEDBACK" : "",
                    "ANSWER_SPECIFIC_FEEDBACK"  : "",
                    "ANSWER_CORRECT_FLG"        : "",
                    "POINTS_EARNED"             : 0,
                    "QUESTION_GRADED_COMMENTS"  : "",
                    "QUESTION_GRADED_BY"        : ""
                }
            }
        },
        ...mapGetters(['selectedStudent']),
        studentAnswer(){
            return this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] ? this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] : [this.initBlankAnswer()];
        },
        testEnded (){
            /* test is ended if teacher ends it or (single sitting and student completed the test)*/
            return this.testPreview.TEST.TEST_ENDED_FLG || (this.testPreview.STUDENT_ATTEMPT.TEST_END_DATE && this.testPreview.TEST.SINGLE_SITTING_COMPLETION_FLG);
        },
        testExpired (){
            /* if now is not within the open & closed window for the test */
            return this.testPreview.TEST.TEST_EXPIRED_FLG;
        },

        activeQuestionGroup (){
            return this.testPreview.QUESTION_GROUPS.filter( grp =>{
                return grp.GROUP_ID === this.activeQuestion.GROUP_ID; 
            } )[ 0 ];
        }

    },
    methods: {
        ...mapActions( [ "load","submitTest" ] ),
        ...mapMutations( [ "updateState","setAnswer" ] ),
        initBlankAnswer(){
            var questionAnswer = JSON.parse( JSON.stringify( this.blankAnswer ) );
            questionAnswer.TEST_QUESTION_RID = this.activeQuestion.TEST_QUESTION_RID;
            return questionAnswer;
        },
        beginTest (){
            this.initiateStartTime();
            // apply start time to student attempt
            this.testPreview.STUDENT_ATTEMPT.TEST_BEG_DATE = this.testStartTime;
            let firstQuestion = this.testPreview.QUESTIONS.findIndex(item=>{
                return !item.hasOwnProperty("TEST_QUESTION_PARENT_RID") || item.TEST_QUESTION_PARENT_RID === "" || item.TEST_QUESTION_PARENT_RID === 0;
            });
            this.updateState( { key: "activeQuestion", value: this.testPreview.QUESTIONS[ firstQuestion ] } );
        },
        activateTimer (){
            this.decrementTime();
        },
        decrementTime (){
            let payload ={
                    routeParams: {
                        action: "time-left",
                        testRid:this.$route.params.testRid
                    },
                    query:{
                        student_rid: this.$route.query.student_rid,
                        school_id: this.$route.query.school_id,
                        course_id: this.$route.query.course_id,
                        section_id: this.$route.query.section_id,
                        teacher_rid: this.$route.query.teacher_rid
                    }
            };
            let TestTaker = new TestTakerResource(payload);
    
            let promise = TestTaker.get();
            
            promise.then((result)=>{
                this.testTimeRemaining = result.data;
                if( this.testTimeRemaining === 0 ){
                    this.updateState( { key: "timeLimitExceeded", value: true } );
                }else{
                    setTimeout( this.decrementTime, 60000 );
                }
            }).catch(result=>{
                setTimeout( this.decrementTime, 60000 );
            });
        },
        cancelTimer (){
            this.testTimeRemaining = 0;
        },
        initiateStartTime (){
            let queryParams = {
                student_rid: this.$route.query.student_rid,
                school_id: this.$route.query.school_id,
                course_id: this.$route.query.course_id,
                section_id: this.$route.query.section_id,
                teacher_rid: this.$route.query.teacher_rid
            };
            this.load( 
                { 
                    "key": "testAttemptStartTime",
                    "routeParams": {
                        action: "init-test",
                        testRid:this.$route.params.testRid
                    },
                    "queryParams": queryParams
                } 
            ).then( ()=>{
                    this.testStarted = true;
                    this.activateTimer();
            } );
        },
        saveAnswer ( question ){
           var payload = {};
            payload.multipleAnswers = this.activeQuestion.MULTIPLE_ANSWER_FLG;
            payload.questionId = this.activeQuestion.TEST_QUESTION_RID;
            payload.answer = question.answer;
            this.setAnswer( payload );
        },
        doSubmit (){
            this.submitTest();
            this.cancelTimer();
        },
        loadTest (){
            let queryParams = {
                student_rid: this.$route.query.student_rid,
                school_id: this.$route.query.school_id,
                course_id: this.$route.query.course_id,
                section_id: this.$route.query.section_id,
                teacher_rid: this.$route.query.teacher_rid
            };

            this.load( 
                { 
                    "key": "testPreview",
                    "queryParams": queryParams, 
                    "routeParams":{ action: "test", testRid: this.$route.params.testRid } 
                }
            )
            .then( ()=>{
                this.editMode = "preview";
                let flaggedQuests = this.testPreview.QUESTIONS.filter(item=>{
                    return item.QUESTION_FLAGGED_FLG;
                });
                if(flaggedQuests.length > 0){
                    this.updateState( { key: "flaggedQuestions", value: flaggedQuests.map(item=>{return item.TEST_QUESTION_RID;}) } );
                }
                if(this.testPreview.TEST.STUDENT_IMMEDIATE_FEEDBACK){
                    this.updateState( { key: "totalPointsEarned", value: this.testPreview.STUDENT_ATTEMPT.TOTAL_SCORE});
                }

            });
        },
        closeWindow (){
            window.close();
        },
        ping(){
            let payload ={
                    routeParams: {
                        action: "ping-test",
                        testRid:this.$route.params.testRid
                    },
                    query:{
                        student_rid: this.$route.query.student_rid,
                        school_id: this.$route.query.school_id,
                        course_id: this.$route.query.course_id,
                        section_id: this.$route.query.section_id,
                        teacher_rid: this.$route.query.teacher_rid
                    }
            };
            let TestTaker = new TestTakerResource(payload);

            let promise = TestTaker.get();
        }
    },
    watch: {
        activeQuestion: {
            immediate: true,
            handler: function(newValue, oldValue) {
                this.ping(false);
            },
        },
    },
    mounted (){
        this.loadTest();
    }
};
</script>

<style scoped>
    button{
        color:#41505f;
    }
</style>
